import React from "react";
import {
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Checkbox,
  VStack,
  HStack,
  Stack,
  Text,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Portal,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";

const Inputs = ({ field, value, onChange }) => {
  const { key, label, type, placeholder, required, options = [] } = field;

  // All hooks must be called at the top level
  const inputBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const hoverBg = useColorModeValue("gray.100", "gray.600");
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const iconColor = useColorModeValue("gray.500", "gray.400");
  const menuListBg = useColorModeValue("white", "gray.800");

  const isOptionsObjects = options.length > 0 && typeof options[0] === "object";
  const getValue = (opt) => (isOptionsObjects ? opt.value || opt.label || "" : opt);
  const getLabel = (opt) => (isOptionsObjects ? opt.label || opt.value || "" : opt);

  // Determine if options should be in a row (horizontal) or column (vertical)
  // Check if all options can fit in one line (short text)
  const shouldBeHorizontal = options.length <= 4 && options.every(opt => {
    const label = getLabel(opt);
    return typeof label === 'string' && label.length <= 30;
  });

  const renderLabel = () => (
    <FormLabel htmlFor={key} fontWeight="600" color={labelColor}>
      {label}
      {required && (
        <Text as="span" color="red.500" ml={1}>
          *
        </Text>
      )}
    </FormLabel>
  );

  switch (type) {
    case "number":
    case "text":
      return (
        <FormControl id={key} isRequired={required}>
          {renderLabel()}
          <Input
            type={type}
            placeholder={placeholder}
            value={value || ""}
            onChange={(e) =>
              type === "number"
                ? onChange(key, e.target.value.replace(/\D/g, ""))
                : onChange(key, e.target.value)
            }
            bg={inputBg}
            borderColor={borderColor}
            rounded="md"
            variant="filled"
            size="md"
            _hover={{ bg: hoverBg }}
            _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #3182ce" }}
          />
        </FormControl>
      );

    case "select":
      return (
        <FormControl id={key} isRequired={required}>
          {renderLabel()}
          <Menu autoSelect={false} closeOnSelect={true}>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              textAlign="left"
              borderRadius="md"
              bg={inputBg}
              borderColor={borderColor}
              borderWidth="1px"
              _hover={{ bg: hoverBg }}
              _expanded={{ 
                bg: hoverBg,
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px #3182ce"
              }}
              _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #3182ce" }}
              width="100%"
              height="40px"
              fontWeight="normal"
              justifyContent="space-between"
              fontSize="md"
              variant="outline"
              transition="all 0.2s"
              sx={{
                '& .chakra-button__icon': {
                  marginLeft: 'auto',
                  marginRight: 0,
                  transition: 'transform 0.2s',
                  color: iconColor,
                },
                '&[aria-expanded="true"] .chakra-button__icon': {
                  transform: 'rotate(180deg)',
                }
              }}
            >
              <Text isTruncated color={value ? labelColor : "gray.500"}>
                {value ? getLabel(options.find(opt => getValue(opt) === value)) : (placeholder || "Select option")}
              </Text>
            </MenuButton>
            <Portal>
              <MenuList
                zIndex="9999"
                maxHeight="200px"
                overflowY="auto"
                borderRadius="md"
                borderColor={borderColor}
                boxShadow="xl"
                bg={menuListBg}
                border="1px solid"
                minW="100%"
                w="auto"
                py={1}
              >
                {options.map((option, index) => (
                  <MenuItem 
                    key={index} 
                    onClick={() => onChange(key, getValue(option))}
                    _hover={{ bg: hoverBg }}
                    _focus={{ bg: hoverBg }}
                    fontSize="md"
                    color={labelColor}
                    fontWeight="normal"
                    minHeight="40px"
                    py={3}
                    px={4}
                    display="flex"
                    alignItems="center"
                    cursor="pointer"
                  >
                    <Text color={labelColor} fontSize="md">
                      {getLabel(option)}
                    </Text>
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </FormControl>
      );

    case "radio": {
      // FIX: Chakra ka RadioGroup sirf EXACT string match par hi
      // radio ko "checked" dikhata hai (RadioGroup value === Radio value).
      // Kuch saved records me value option ke value se thoda alag format
      // me save ho jaata hai (extra space, ya different case) — data
      // present hone ke bawajood koi bhi radio checked nahi dikhta tha.
      // Ab match case-insensitive + trim-tolerant hai: agar koi option
      // milta hai to uska EXACT/canonical value RadioGroup ko diya jaata
      // hai (taaki highlight sahi ho), warna pehle jaisa hi behavior
      // (kuch checked nahi) rehta hai — already-working exact matches
      // par koi asar nahi padta.
      const rawValue = typeof value === "string" ? value.trim() : value;
      const matchedOption = options.find((opt) => {
        const optVal = getValue(opt);
        return (
          typeof optVal === "string" &&
          typeof rawValue === "string" &&
          optVal.trim().toLowerCase() === rawValue.toLowerCase()
        );
      });
      const radioValue = matchedOption ? getValue(matchedOption) : rawValue || "";

      return (
        <FormControl id={key} isRequired={required}>
          {renderLabel()}
          <RadioGroup value={radioValue} onChange={(val) => onChange(key, val)}>
            <Stack 
              direction={shouldBeHorizontal ? "row" : "column"} 
              spacing={shouldBeHorizontal ? 6 : 2}
              align="start"
            >
              {options.map((option, index) => (
                <Radio key={index} value={getValue(option)}>
                  {getLabel(option)}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FormControl>
      );
    }

    case "checkbox":
      return (
        <FormControl id={key} isRequired={required}>
          {renderLabel()}
          <Stack 
            direction={shouldBeHorizontal ? "row" : "column"} 
            spacing={shouldBeHorizontal ? 6 : 2}
            align="start"
          >
            {options.map((option, index) => {
              const optValue = getValue(option);
              // Ensure value is always an array for checkbox fields
              const currentValue = Array.isArray(value) ? value : (value ? [value] : []);
              
              return (
                <Checkbox
                  key={index}
                  isChecked={currentValue.includes(optValue)}
                  onChange={() => {
                    let newValue;
                    
                    if (currentValue.includes(optValue)) {
                      // Remove the value
                      newValue = currentValue.filter((v) => v !== optValue);
                    } else {
                      // Add the value
                      newValue = [...currentValue, optValue];
                    }
                    
                    // Always pass an array for checkbox fields
                    onChange(key, newValue);
                  }}
                >
                  {getLabel(option)}
                </Checkbox>
              );
            })}
          </Stack>
        </FormControl>
      );

    default:
      return null;
  }
};

export default Inputs;